import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, RefreshCw, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useEvaluationBreakdown } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "./interactive-legend";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_API_MAP: Record<SpeciesFilter, string | undefined> = {
  all: undefined,
  dogs: "DOG",
  cats: "CAT",
  other: undefined,
};

const EVAL_KEYS = ["complete", "needsReview", "needsRecs", "unknown", "error", "noStatus"] as const;

const EVAL_COLORS: Record<string, string> = {
  complete: "hsl(142 70% 45%)",
  needsReview: "hsl(38 92% 50%)",
  needsRecs: "hsl(210 70% 50%)",
  unknown: "hsl(220 10% 55%)",
  error: "hsl(0 72% 51%)",
  noStatus: "hsl(280 10% 65%)",
};

const EVAL_LABELS: Record<string, string> = {
  complete: "Complete",
  needsReview: "Needs Review",
  needsRecs: "Needs Recs",
  unknown: "Unknown",
  error: "Error",
  noStatus: "No Status",
};

interface EvaluationBreakdownChartProps {
  days: number;
}

function StatCard({ label, value, color, testId }: { label: string; value: number; color?: string; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

export function EvaluationBreakdownChart({ days }: EvaluationBreakdownChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");
  const [showNoStatus, setShowNoStatus] = useState(false);

  const apiSpecies = SPECIES_API_MAP[speciesFilter];
  const { data, isLoading, isError, refetch } = useEvaluationBreakdown(days, location, apiSpecies);

  const allPieData = useMemo(() => {
    if (!data?.current) return [];
    const current = speciesFilter === "other" && data.current.bySpecies?.OTHER
      ? data.current.bySpecies.OTHER
      : data.current;
    return EVAL_KEYS
      .filter((key) => showNoStatus || key !== "noStatus")
      .map((key) => ({
        name: EVAL_LABELS[key],
        value: (current as any)[key] || 0,
        color: EVAL_COLORS[key],
      }))
      .filter(d => d.value > 0);
  }, [data, speciesFilter, showNoStatus]);

  const legendSeries = useMemo<LegendSeries[]>(() =>
    allPieData.map(d => ({ key: d.name, label: d.name, color: d.color })),
    [allPieData]
  );

  const { visibleSeries, toggleSeries } = useToggleableSeries(legendSeries);

  const pieData = useMemo(
    () => allPieData.filter(d => visibleSeries.has(d.name)),
    [allPieData, visibleSeries]
  );

  const currentStats = useMemo(() => {
    if (!data?.current) return null;
    if (speciesFilter === "other" && data.current.bySpecies?.OTHER) {
      return { ...data.current.bySpecies.OTHER };
    }
    return data.current;
  }, [data, speciesFilter]);

  const chartTitle = "Evaluation Breakdown";

  if (isLoading) {
    return (
      <Card data-testid="card-evaluation-breakdown">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            {chartTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="card-evaluation-breakdown">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            {chartTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-2">
              Failed to load evaluation data.
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-eval-breakdown">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-evaluation-breakdown">
      <CardHeader className="flex flex-row items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            {chartTitle}
          </CardTitle>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch
              id="show-no-status"
              checked={showNoStatus}
              onCheckedChange={setShowNoStatus}
              data-testid="switch-show-no-status"
            />
            <Label htmlFor="show-no-status" className="text-sm text-muted-foreground whitespace-nowrap">
              Show No Status
            </Label>
          </div>
          <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
            <SelectTrigger className="w-[130px]" data-testid="select-eval-species">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Species</SelectItem>
              <SelectItem value="dogs">Dogs</SelectItem>
              <SelectItem value="cats">Cats</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {currentStats && (
          <div className="mb-4" data-testid="eval-stats">
            <p className="text-xs text-muted-foreground mb-1" data-testid="text-eval-stats-hint">
              Current snapshot
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              <StatCard
                label={showNoStatus ? "Total Eval." : "Total (w/ Status)"}
                value={showNoStatus
                  ? (currentStats.total || EVAL_KEYS.reduce((s, k) => s + ((currentStats as any)[k] || 0), 0))
                  : EVAL_KEYS.filter(k => k !== "noStatus").reduce((s, k) => s + ((currentStats as any)[k] || 0), 0)
                }
                testId="stat-eval-total"
              />
              {EVAL_KEYS
                .filter((key) => showNoStatus || key !== "noStatus")
                .map((key) => (
                <StatCard
                  key={key}
                  label={EVAL_LABELS[key]}
                  value={(currentStats as any)[key] || 0}
                  color={EVAL_COLORS[key]}
                  testId={`stat-eval-${key.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        )}

        {allPieData.length > 0 ? (
          <>
            <InteractiveLegend
              series={legendSeries}
              visibleSeries={visibleSeries}
              onToggle={toggleSeries}
              testIdPrefix="eval-legend"
            />
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={showNoStatus
                    ? false
                    : ({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    borderColor: "hsl(var(--border))",
                    borderRadius: "6px",
                    color: "hsl(var(--card-foreground))",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground" data-testid="text-eval-empty">
            No evaluation data found for the selected filters.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
